import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiPost } from '@/lib/http/client';

/**
 * Deterministic JSON serialization with sorted object keys.
 * Produces a stable string regardless of property insertion order,
 * so React Query cache keys are stable even when callers pass new
 * object references with the same content.
 *
 * Exported so callers that use useQuery directly (e.g. for advanced options
 * like placeholderData) can share the same key format.
 */
export function stableStringify(obj: unknown): string {
  return JSON.stringify(obj, (_key, value: unknown) => {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return Object.keys(value as object)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          acc[k] = (value as Record<string, unknown>)[k];
          return acc;
        }, {});
    }
    return value;
  });
}

interface UseCrmAggregateOptions {
  enabled?: boolean;
  staleTime?: number;
}

/**
 * Builds the shared `crmAggregate` query key. All callers share a single
 * `['crmAggregate', orgId, stableHash(requestBody)]` key family, so
 * WebSocket invalidation in applyEvent.ts covers every aggregate query
 * (dashboard counts, pipeline metrics, column totals) with one predicate
 * branch — and the allowForcedRefetch throttle prevents storms.
 */
export function getCrmAggregateQueryKey(
  orgId: number | undefined,
  requestBody: object
) {
  return ['crmAggregate', orgId, stableStringify(requestBody)] as const;
}

/**
 * Generic primitive for CRM aggregate POST requests.
 *
 * Callers needing advanced React Query options (e.g. placeholderData) should
 * use useQuery directly with getCrmAggregateQueryKey to build the same key:
 *   queryKey: getCrmAggregateQueryKey(orgId, requestBody)
 */
export function useCrmAggregate<TResponse>(
  orgId: number | undefined,
  requestBody: object,
  options?: UseCrmAggregateOptions
): UseQueryResult<TResponse, Error> {
  return useQuery<TResponse, Error>({
    queryKey: getCrmAggregateQueryKey(orgId, requestBody),
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No org ID');
      }
      return apiPost<TResponse>(
        `/api/orgs/${orgId}/crm-events/crm-objects/aggregate/`,
        requestBody
      );
    },
    enabled:
      options?.enabled !== undefined ? options.enabled && !!orgId : !!orgId,
    staleTime: options?.staleTime ?? 5 * 60 * 1000,
  });
}
